import HttpRequest from "../../utilities/httpRequest";
import {
  hideLoader,
  showLoader,
  showSnackbar,
} from "../../utilities/UtilitiesActions";
import {
  addCase,
  addLogItem,
  getCaseById,
  getCasePriorities,
  getCases,
  getCaseStatus,
  getCaseStatusForDoctor,
  getClosedCases,
  getDoctorsOfDept,
  getLogItemReadStatus,
  getLogItemsByCaseId,
  getOpenCases,
  getPatientCases,
  readLogitem,
  updateCase,
  updateCaseDoctors,
  getAllSymptoms,
} from "./CasesActions";

/**
 * function to get cases
 * @returns {function(*=): Promise<unknown>}
 * @param id
 */
export const getCasesRequest = () => {
  const data = {
    status: "ALL",
  };
  return (dispatch) => {
    dispatch(showLoader());
    const url = `case?searchData=${JSON.stringify(data)}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getCases(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get cases for specific patient
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getPatientCasesRequest = (patientId) => {
  return (dispatch) => {
    dispatch(showLoader());
    const data = {
      patientId: patientId,
    };

    const url = `case?searchData=${JSON.stringify(data)}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getPatientCases(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get open cases
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getOpenCasesRequest = () => {
  const data = {
    status: "OPEN",
  };
  return (dispatch) => {
    dispatch(showLoader());
    const url = `case?searchData=${JSON.stringify(data)}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getOpenCases(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get closed cases
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getClosedCasesRequest = () => {
  const data = {
    status: "CLOSED",
  };
  return (dispatch) => {
    dispatch(showLoader());
    const url = `case?searchData=${JSON.stringify(data)}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getClosedCases(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get case by id
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getCaseByIdRequest = (id) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `case/${id}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getCaseById(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to add case
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const addCaseRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = "case";

    return new HttpRequest(dispatch, url, "post", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Case created successfully", "success"));

        dispatch(addCase(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to update case
 * @param id: id for api request
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const updateCaseRequest = (id, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `case/${id}`;

    return new HttpRequest(dispatch, url, "patch", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Case updated successfully", "success"));
        dispatch(updateCase(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to update case doctors
 * @param caseId: caseId for api request
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const updateCaseDoctorsRequest = (caseId, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `addCaregiverToCase/${caseId}`;

    return new HttpRequest(dispatch, url, "patch", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Doctor list updated.", "success"));

        dispatch(updateCaseDoctors(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get case priorities
 * @returns {function(*=): Promise<unknown>}
 */
export const getCasePrioritiesRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `casePriority`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());

        dispatch(getCasePriorities(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get case status
 * @returns {function(*=): Promise<unknown>}
 */
export const getCaseStatusRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `caseStatus`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getCaseStatus(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get case status for healthcare provider organizations
 * @returns {function(*=): Promise<unknown>}
 */
export const getCaseStatusForDoctorRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `docCaseStatus`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getCaseStatusForDoctor(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get doctors of departments
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getDoctorsOfDeptRequest = (id) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/${id}/caregiver`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getDoctorsOfDept(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get log items by case id
 * @param id: id for api request
 * @param type: type for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getLogItemsByCaseIdRequest = (id, type) => {
  return (dispatch) => {
    dispatch(showLoader());
    const searchType = {
      type: type,
    };
    const url = `case/${id}/logitem?searchData=${JSON.stringify(searchType)}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getLogItemsByCaseId(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to add log item
 * @param caseId: caseId for api request
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const addLogItemRequest = (caseId, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `logitem/${caseId}`;

    return new HttpRequest(dispatch, url, "post", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(addLogItem(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get log item read status
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getLogItemReadStatusRequest = (id) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `logitem/${id}/readStatus`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getLogItemReadStatus(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to read log item
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const readLogItemRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());

    const url = `readLogs`;

    return new HttpRequest(dispatch, url, "post", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(readLogitem(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get signed url
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getSignedUrlRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `patients/profilePic/signedURL`;
    return new HttpRequest(dispatch, url, "post", data)
      .send()
      .then((res) => {
        dispatch(hideLoader());
        return Promise.resolve(res.data.data);
      })
      .catch((error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      });
  };
};

/**
 * function to upload file to signed url
 * @param url: url for api request
 * @param file: file for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const uploadToS3Request = (url, file) => {
  return (dispatch) => {
    dispatch(showLoader());
    return new HttpRequest(dispatch, url, "put", file)
      .s3Upload()
      .then((res) => {
        dispatch(hideLoader());
        return Promise.resolve(res);
      })
      .catch((error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      });
  };
};

export const getAllSymptomsRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `patient/symptom/all`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());

        dispatch(getAllSymptoms(res.data));
        return Promise.resolve(res.data);
      },
      (err) => {
        dispatch(hideLoader());
        return Promise.reject(err);
      }
    );
  };
};
